import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  ClockIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  EyeIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { DuplicateIconButton } from '../../components/ui/DuplicateIconButton';
import { VisibilityIconButton } from '../../components/ui/VisibilityIconButton';
import { DocumentationPageStatus } from '../../services/documentationApi';

export type AutosaveIndicator = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

type DocumentationPageToolbarProps = {
  canWrite: boolean;
  isEditing: boolean;
  status: DocumentationPageStatus | string;
  isPublic: boolean;
  autosave: AutosaveIndicator;
  versionLabel?: string | null;
  hasUnpublishedChanges?: boolean;
  liveViewHref?: string | null;
  detailsOpen: boolean;
  onToggleDetails: () => void;
  onToggleVisibility: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  onArchive: () => void;
  onDuplicate: () => void;
  onExportPdf: () => void;
  onOpenVersions: () => void;
};

function statusLabel(status: string): string {
  if (status === 'Published') return 'Publicado';
  if (status === 'Archived') return 'Arquivado';
  if (status === 'Expired') return 'Expirado';
  return 'Rascunho';
}

function statusToneClass(status: string): string {
  if (status === 'Published') return 'docs-status--published';
  if (status === 'Archived') return 'docs-status--archived';
  if (status === 'Expired') return 'docs-status--expired';
  return 'docs-status--draft';
}

function autosaveLabel(state: AutosaveIndicator): string | null {
  if (state === 'saving') return 'Salvando…';
  if (state === 'saved') return 'Salvo';
  if (state === 'dirty') return 'Não salvo';
  if (state === 'error') return 'Erro ao salvar';
  return null;
}

export function DocumentationPageToolbar({
  canWrite,
  isEditing,
  status,
  isPublic,
  autosave,
  versionLabel,
  hasUnpublishedChanges,
  liveViewHref,
  detailsOpen,
  onToggleDetails,
  onToggleVisibility,
  onEdit,
  onCancel,
  onSaveDraft,
  onPublish,
  onArchive,
  onDuplicate,
  onExportPdf,
  onOpenVersions,
}: DocumentationPageToolbarProps) {
  const saveHint = autosaveLabel(autosave);
  const isDraft = status === 'Draft';
  const isPublished = status === 'Published';
  const canArchive = status === 'Published' || status === 'Expired';
  const canPublish = isEditing || isDraft || Boolean(hasUnpublishedChanges);
  const canViewLive = isPublished && Boolean(liveViewHref);

  return (
    <div className="docs-wiki-toolbar">
      <div className="docs-wiki-toolbar__left">
        <span
          className={`docs-status docs-status--icon ${statusToneClass(status)}`}
          title={statusLabel(status)}
        >
          <DocumentTextIcon className="h-4 w-4" aria-hidden />
          <span className="docs-status__text">{statusLabel(status)}</span>
        </span>
        {versionLabel ? (
          <span className="docs-status" title="Versão">
            v{versionLabel}
            {hasUnpublishedChanges ? ' · rascunho' : ''}
          </span>
        ) : null}
        {canWrite ? (
          <VisibilityIconButton isPublic={isPublic} onClick={onToggleVisibility} disabled={false} />
        ) : (
          <span className="docs-status" title={isPublic ? 'Pública' : 'Privada'}>
            {isPublic ? 'Pública' : 'Privada'}
          </span>
        )}
        {isEditing && saveHint ? (
          <span
            className={`docs-autosave${autosave === 'error' ? ' docs-autosave--error' : ''}${
              autosave === 'saved' ? ' docs-autosave--ok' : ''
            }`}
          >
            {saveHint}
          </span>
        ) : null}
      </div>

      <div className="docs-wiki-toolbar__actions">
        <button
          type="button"
          className={`docs-icon-btn${detailsOpen ? ' is-active' : ''}`}
          title="Detalhes"
          aria-label="Detalhes"
          aria-pressed={detailsOpen}
          onClick={onToggleDetails}
        >
          <InformationCircleIcon className="h-5 w-5" />
        </button>

        <button
          type="button"
          className="docs-icon-btn"
          title="Versões"
          aria-label="Versões"
          onClick={onOpenVersions}
        >
          <ClockIcon className="h-5 w-5" />
        </button>

        {canViewLive ? (
          <a
            href={liveViewHref!}
            target="_blank"
            rel="noopener noreferrer"
            className="docs-icon-btn"
            title="Visualizar"
            aria-label="Visualizar"
          >
            <EyeIcon className="h-5 w-5" />
          </a>
        ) : null}

        {canWrite ? (
          <>
            <DuplicateIconButton label="Fazer cópia" onClick={onDuplicate} />
            <button
              type="button"
              className="docs-icon-btn"
              title="Exportar PDF"
              aria-label="Exportar PDF"
              onClick={onExportPdf}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>

            {isEditing ? (
              <>
                <button
                  type="button"
                  className="docs-icon-btn"
                  title="Cancelar"
                  aria-label="Cancelar"
                  onClick={onCancel}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  className="docs-icon-btn"
                  title="Salvar rascunho"
                  aria-label="Salvar rascunho"
                  onClick={onSaveDraft}
                >
                  <CheckIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  className="docs-icon-btn"
                  title="Publicar"
                  aria-label="Publicar"
                  onClick={onPublish}
                >
                  <CloudArrowUpIcon className="h-5 w-5" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="docs-icon-btn"
                  title="Editar"
                  aria-label="Editar"
                  onClick={onEdit}
                >
                  <PencilSquareIcon className="h-5 w-5" />
                </button>
                {canArchive ? (
                  <button
                    type="button"
                    className="docs-icon-btn"
                    title="Arquivar"
                    aria-label="Arquivar"
                    onClick={onArchive}
                  >
                    <ArchiveBoxIcon className="h-5 w-5" />
                  </button>
                ) : null}
                {canPublish ? (
                  <button
                    type="button"
                    className="docs-icon-btn"
                    title="Publicar"
                    aria-label="Publicar"
                    onClick={onPublish}
                  >
                    <CloudArrowUpIcon className="h-5 w-5" />
                  </button>
                ) : null}
              </>
            )}
          </>
        ) : (
          <button
            type="button"
            className="docs-icon-btn"
            title="Exportar PDF"
            aria-label="Exportar PDF"
            onClick={onExportPdf}
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
